import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import PlatformText from './PlatformText';

const ErrorToast = ({ 
  error, 
  onDismiss,
  autoHide = true, 
  duration = 5000,
  variant = 'error' // 'error', 'warning', 'info'
}) => {
  const { t } = useTranslation('errors');
  const [isVisible, setIsVisible] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 300); // Allow exit animation to complete
  }, [onDismiss]);

  useEffect(() => {
    // Trigger entrance animation
    const entranceTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50); // Small delay to trigger entrance animation

    if (autoHide && duration > 0) {
      const autoHideTimer = setTimeout(() => {
        handleDismiss();
      }, duration);
      return () => {
        clearTimeout(entranceTimer);
        clearTimeout(autoHideTimer);
      };
    }

    return () => clearTimeout(entranceTimer);
  }, [autoHide, duration, handleDismiss]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          backgroundColor: 'var(--warning-color)',
          borderColor: 'var(--warning-hover)',
          icon: '⚠️',
          textColor: 'white'
        };
      case 'info':
        return {
          backgroundColor: 'var(--primary-color)',
          borderColor: 'var(--primary-hover)',
          icon: 'ℹ️',
          textColor: 'white'
        };
      default: // error
        return {
          backgroundColor: 'var(--danger-color)',
          borderColor: 'var(--danger-hover)',
          icon: '❌',
          textColor: 'white'
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Build CSS class name following platform component pattern
  const getClassName = () => {
    const baseClass = 'error-toast';
    const visibleClass = isVisible ? 'visible' : 'hidden';
    const variantClass = `variant-${variant}`;
    
    return [baseClass, visibleClass, variantClass]
      .filter(Boolean)
      .join(' ');
  };

  // Minimal inline styles - only for dynamic values (variants)
  const getInlineStyles = () => {
    return {
      backgroundColor: variantStyles.backgroundColor,
      border: `1px solid ${variantStyles.borderColor}`,
      color: variantStyles.textColor
    };
  };

  return (
    <div
      className={getClassName()}
      style={getInlineStyles()}
    >
      {/* Error Icon */}
      <div className="error-toast-icon">
        {variantStyles.icon}
      </div>

      {/* Error Content */}
      <div className="error-toast-content">
        <PlatformText
          variant="body"
          className="error-toast-message"
          style={{ 
            color: variantStyles.textColor,
            fontWeight: '500',
            wordBreak: 'break-word',
            lineHeight: '1.4',
            margin: 0
          }}
        >
          {error.replace(/\.$/, '')}
        </PlatformText>
      </div>

      {/* Close Button */}
      <button
        className="error-toast-close"
        onClick={handleDismiss}
        style={{
          color: variantStyles.textColor
        }}
        aria-label={t('general.dismiss')}
      >
        ×
      </button>
    </div>
  );
};

export default ErrorToast;
